import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api-client";
import { CONTACT_STATUSES, type ContactStatus, type LeadTemperature } from "@ai-leasing-os/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { TemperaturePill } from "@/components/badges/TemperaturePill";
import { formatIdr, formatRelative } from "@/lib/format";
import { useState } from "react";
import { cn } from "@/lib/cn";
import { Upload, Users } from "lucide-react";
import { CsvImporter } from "@/components/import/CsvImporter";
import { EmptyState } from "@/components/ui/empty-state";

interface Row {
  id: string;
  display_name: string | null;
  primary_phone: string | null;
  primary_email: string | null;
  status: ContactStatus;
  score: number;
  awareness_level: string;
  temperature: LeadTemperature;
  /** JSON column — TiDB returns it as a parsed array or a string. */
  tags: string[] | string | null;
  extracted_preferred_area: string | null;
  extracted_budget_max_idr: number | null;
  extracted_move_in_date: string | null;
  extracted_bedrooms: number | null;
  updated_at: string;
}

/** TiDB returns JSON columns as a parsed array OR a raw string. Normalize. */
function parseTags(raw: string[] | string | null): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((t) => typeof t === "string");
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t) => typeof t === "string") : [];
  } catch {
    return [];
  }
}

export function ContactsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ContactStatus | null>(null);
  const [scoreMin, setScoreMin] = useState<number | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["contacts", statusFilter, scoreMin],
    queryFn: () => {
      const params = new URLSearchParams({ limit: "200" });
      if (statusFilter) params.set("status", statusFilter);
      if (scoreMin !== null) params.set("score_min", String(scoreMin));
      return api.get<Row[]>(`/api/contacts?${params.toString()}`);
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Contacts</h1>
          <p className="text-sm text-slate-500">
            All leads and clients. Click a row to open the contact detail.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
          <Upload className="h-4 w-4" /> Import CSV
        </Button>
      </header>

      <CsvImporter
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ["contacts"] })}
        endpoint="/api/contacts/import"
        entityLabel="Contacts"
        columns={[
          { key: "display_name", required: true, example: "Lucas Martin", help: "name shown in the UI" },
          { key: "primary_phone", required: true, example: "+33612345678", help: "E.164, used for de-dup" },
          { key: "primary_email", example: "lucas@example.com" },
          { key: "nationality", example: "French" },
          { key: "preferred_contact_language", example: "fr", help: "ISO 639-1, ex: en, fr, id" },
          { key: "status", example: "prospect", help: "prospect/lead/qualified/..." },
          { key: "score", example: "0", help: "0-100" },
          { key: "extracted_preferred_area", example: "Canggu" },
          { key: "extracted_bedrooms", example: "3" },
          { key: "extracted_budget_max_idr", example: "40000000" },
          { key: "extracted_move_in_date", example: "2026-07-01", help: "YYYY-MM-DD" },
          { key: "internal_notes", example: "VIP from referral" },
        ]}
        coerceRow={(raw) => {
          const out: Record<string, unknown> = { ...raw };
          for (const k of ["score", "extracted_budget_min_idr", "extracted_budget_max_idr", "extracted_bedrooms", "extracted_length_months"]) {
            if (raw[k] !== undefined && raw[k] !== "") out[k] = Number(raw[k]);
          }
          return out;
        }}
      />

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">
          status
        </span>
        <button
          onClick={() => setStatusFilter(null)}
          className={cn(
            "px-2 py-0.5 rounded text-[11px] font-medium",
            statusFilter === null ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}
        >
          all
        </button>
        {CONTACT_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s === statusFilter ? null : s)}
            className={cn(
              "px-2 py-0.5 rounded text-[11px] font-medium",
              statusFilter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {s}
          </button>
        ))}
        <span className="ml-4 text-[10px] uppercase tracking-wider text-slate-400">
          score ≥
        </span>
        {[null, 30, 60, 80].map((v) => (
          <button
            key={String(v)}
            onClick={() => setScoreMin(v)}
            className={cn(
              "px-2 py-0.5 rounded text-[11px] font-medium",
              scoreMin === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {v === null ? "any" : v}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">
          {rows.length} contacts
        </span>
      </Card>

      <Card>
        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          statusFilter || scoreMin !== null ? (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="No contacts match these filters"
              description="Try clearing the status or score filter."
              action={{
                label: "Clear filters",
                onClick: () => {
                  setStatusFilter(null);
                  setScoreMin(null);
                },
              }}
            />
          ) : (
            <EmptyState
              icon={<Users className="h-6 w-6" />}
              title="No contacts yet"
              description="Leads who message you on any channel show up here automatically. You can also import an existing list."
              action={{
                label: "Import CSV",
                onClick: () => setImportOpen(true),
                icon: <Upload className="h-4 w-4" />,
              }}
            />
          )
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <Th>Name</Th>
                <Th>Contact</Th>
                <Th>Status</Th>
                <Th>Temp</Th>
                <Th>Score</Th>
                <Th>Area</Th>
                <Th>Budget</Th>
                <Th>Move-in</Th>
                <Th>BR</Th>
                <Th>Updated</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const tags = parseTags(c.tags);
                return (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <Td>
                    <Link
                      to={`/contacts/${c.id}`}
                      className="font-medium text-slate-900 hover:underline"
                    >
                      {c.display_name || "(no name)"}
                    </Link>
                    {tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {tags.slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="inline-block rounded bg-brand-soft px-1.5 py-0.5 text-[10px] font-medium text-brand-primary"
                          >
                            {t}
                          </span>
                        ))}
                        {tags.length > 4 && (
                          <span className="text-[10px] text-slate-400">
                            +{tags.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </Td>
                  <Td className="text-xs">
                    <div className="font-mono text-slate-500">{c.primary_phone ?? "-"}</div>
                    {c.primary_email && (
                      <a
                        href={`mailto:${c.primary_email}`}
                        className="text-slate-500 hover:text-slate-700 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.primary_email}
                      </a>
                    )}
                  </Td>
                  <Td><StatusBadge status={c.status} /></Td>
                  <Td><TemperaturePill temperature={c.temperature} /></Td>
                  <Td className="font-semibold tabular-nums">{c.score}</Td>
                  <Td className="text-slate-600">{c.extracted_preferred_area ?? "-"}</Td>
                  <Td className="text-slate-600 tabular-nums">{formatIdr(c.extracted_budget_max_idr)}</Td>
                  <Td className="text-slate-600 tabular-nums">{c.extracted_move_in_date ?? "-"}</Td>
                  <Td className="text-slate-600 tabular-nums">{c.extracted_bedrooms ?? "-"}</Td>
                  <Td className="text-slate-400 text-xs">{formatRelative(c.updated_at)}</Td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-semibold">{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2 align-middle", className)}>{children}</td>;
}
